import React, { useEffect, useRef, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { loadGoogleMaps } from '../../utils/googleMaps';
import './StreetViewPanel.css';

interface StreetViewPanelProps {
  lat: number;
  lng: number;
  storeName?: string;
  onClose: () => void;
}

const StreetViewPanel: React.FC<StreetViewPanelProps> = ({ lat, lng, storeName, onClose }) => {
  const streetViewRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !streetViewRef.current) return;
      initPanorama();
    }).catch(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  function initPanorama() {
    if (!streetViewRef.current || !window.google) return;

    try {
      setIsLoading(true);
      const streetView = new window.google.maps.StreetViewPanorama(streetViewRef.current, {
        position: { lat, lng },
        pov: { heading: 0, pitch: 0 },
        zoom: 1,
        enableCloseButton: false,
      });

      streetView.addListener('pano_changed', () => {
        setIsLoading(false);
      });

      streetView.addListener('position_changed', () => {
        if (streetViewRef.current) {
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.error('Error initializing Street View:', error);
      setIsLoading(false);
    }
  }

  return (
    <div className="street-view-panel">
      <div className="street-view-header">
        <div className="street-view-title">
          {storeName ? `360° View - ${storeName}` : '360° Street View'}
        </div>
        <button
          className="street-view-close-btn"
          onClick={onClose}
          title="Close Street View"
          aria-label="Close Street View"
        >
          <MdClose size={24} />
        </button>
      </div>
      <div className="street-view-container">
        {isLoading && (
          <div className="street-view-loading">
            <div className="spinner"></div>
            <p>Loading 360° view...</p>
          </div>
        )}
        <div ref={streetViewRef} className="street-view-panorama" />
      </div>
    </div>
  );
};

export default StreetViewPanel;
